/**
 * DeliveryForecastCard — Vorlauf-Übersicht eintreffender Bestellfahrzeuge.
 *
 * Zeigt Inserate mit gesetztem Liefertermin (`earliest_delivery` als „KW.Jahr")
 * gruppiert: überfällig · diese Woche · nächste 4 Wochen. Quelle:
 * GET /api/dealer/vehicle-arrivals (org-weit). Klick auf ein Fahrzeug öffnet das
 * Inserat; CTA springt in die Disposition mit Liefertermin-Filter.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, AlertTriangle, CalendarClock, ChevronDown } from "lucide-react";
import api from "../../lib/api";

const SECTIONS = [
    { key: "overdue", label: "Überfällig", icon: AlertTriangle, tone: "text-red-600", badge: "bg-red-100 text-red-700" },
    { key: "this_week", label: "Diese Woche", icon: CalendarClock, tone: "text-amber-600", badge: "bg-amber-100 text-amber-800" },
    { key: "next_4_weeks", label: "Nächste 4 Wochen", icon: Truck, tone: "text-emerald-600", badge: "bg-emerald-100 text-emerald-700" },
];

export default function DeliveryForecastCard() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/dealer/vehicle-arrivals")
            .then(({ data }) => { if (alive) setData(data); })
            .catch(() => { if (alive) setData({ overdue: { count: 0, items: [] }, this_week: { count: 0, items: [] }, next_4_weeks: { count: 0, items: [] }, later_count: 0, total: 0 }); });
        return () => { alive = false; };
    }, []);

    if (!data) return null;
    if (data.total === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="delivery-forecast-card">
                <h3 className="font-bold text-sm flex items-center gap-2"><Truck className="w-4 h-4 text-[#111827]" /> Vorlauf — eintreffende Bestellfahrzeuge</h3>
                <p className="text-xs text-gray-600 mt-2" data-testid="delivery-forecast-empty">
                    Keine Bestellfahrzeuge mit Liefertermin hinterlegt. Setze in der Disposition einen „Frühestens"-Termin (KW), dann erscheinen sie hier.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="delivery-forecast-card">
            <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between p-4 text-left" data-testid="delivery-forecast-toggle">
                <h3 className="font-bold text-sm flex items-center gap-2">
                    <Truck className="w-4 h-4 text-[#111827]" /> Vorlauf — eintreffende Bestellfahrzeuge
                    <span className="text-[11px] font-normal text-gray-600">({data.total} mit Liefertermin{data.later_count ? ` · ${data.later_count} später` : ""})</span>
                </h3>
                <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {SECTIONS.map((s) => {
                        const group = data[s.key] || { count: 0, items: [] };
                        const Icon = s.icon;
                        return (
                            <div key={s.key} className="border border-gray-200 rounded-sm" data-testid={`delivery-forecast-${s.key}`}>
                                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                                    <span className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${s.tone}`}><Icon className="w-3.5 h-3.5" /> {s.label}</span>
                                    <span className={`text-[11px] px-2 py-0.5 rounded-sm font-bold ${s.badge}`} data-testid={`delivery-forecast-count-${s.key}`}>{group.count}</span>
                                </div>
                                <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                                    {group.items.length === 0 ? (
                                        <div className="px-3 py-3 text-[11px] text-gray-600 italic">Keine</div>
                                    ) : group.items.map((it) => (
                                        <Link key={it.id} to={`/listings/${it.id}`} className="block px-3 py-2 hover:bg-gray-50" data-testid={`delivery-forecast-item-${it.id}`}>
                                            <div className="text-xs font-medium text-[#111827] truncate">{it.title || `${it.brand || ""} ${it.model || ""}`.trim() || "Fahrzeug"}</div>
                                            <div className="text-[10px] text-gray-500 flex items-center gap-1.5 flex-wrap">
                                                <span>{it.delivery_label}</span>
                                                {it.site_code ? <span className="text-gray-600">· {it.site_code}</span> : null}
                                                {it.dispo_order_code ? <span className="font-mono text-gray-600">· {it.dispo_order_code}</span> : null}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    <div className="md:col-span-3">
                        <Link to="/dealer/disposition" className="text-xs font-bold text-[#111827] hover:underline" data-testid="delivery-forecast-dispo-link">
                            → Alle Bestellfahrzeuge in der Disposition
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}
